import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchUserGroupsUsers, fetchUserGroupsUsersForExport } from 'common/services/api/UserGroupsUsers/userGroupsUsers.thunk';
import { ActionDefinition, Operations } from 'common/types/TypeDefs';
import { userMembershipColumnsDefinitionWithActions } from 'common/utils/userMembershipColumnsHelper';

import { getUserGroupDownFiltersConfiguration } from './helpers';
import { UserGroupsUsersCreateDrawerContent } from './UserGroupsUsersCreateDrawerContent';
import { addNewFilter, clearFilters, removeFilter, selectFilteredStatus, selectFilters } from './userGroupsUsersSlice';
import { UserGroupUsersDeleteDialogContent } from './UserGroupUsersDeleteDialogContent';

export function UserGroupsUsersTab() {
    const selectedFilters = useAppSelector(selectFilters);
    const filteredStatus = useAppSelector(selectFilteredStatus);

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);
    const shouldDisableDeleteButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableCreate = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const [ defaultSortModel ] = usePalmSortModel('principalName');

    const filters = useAppSelector(selectFilters);
    const { suiteId, tenantId = '', usergroupId = '' } = useParams();

    const fetchGridData = usePalmFetchPaginatedEntities(
        'User Group Users',
        { tenantId, usergroupId: usergroupId, status: filteredStatus, skip: 0, take: 0 },
        fetchUserGroupsUsers,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        getUserGroupDownFiltersConfiguration()
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchUserGroupsUsersForExport({
            tenantId: tenantId ?? '',
            usergroupId: usergroupId ?? '',
            search: search ?? '',
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal,
            status: selectedFilters.dropdowns.status.keys
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const onGetRowLink = useCallback((userId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}/${userId}/${Routes.UserGroups}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtons, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableDeleteButton }
    ] as ActionDefinition[];

    const columnDefinitions = userMembershipColumnsDefinitionWithActions;

    return <PalmDataGrid
        title='User'
        processing={processing}
        actionDefinitions={actionDefinitions}
        contentFunction={UserGroupsUsersCreateDrawerContent}
        columnDefinitions={columnDefinitions}
        isCreateButtonDisabled={shouldDisableCreate}
        paginationMode={paginationMode}
        filter={filterProp}
        refreshDataGridClick={refreshDataGrid}
        operationName={Operations.Link}
        deleteFunction={UserGroupUsersDeleteDialogContent}
        customDeleteTitle={'Are you sure you want to remove this User from this User Group?'}
        searchFilterDisabled={filters.keywords.length !== 0}
        getRowLink={shouldDisableButtons ? undefined : onGetRowLink}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT,
        }}
        sortModel={paginationGridData?.sortModel ?? defaultSortModel}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
    />;
}
